import { useState } from 'react';
import { useCreateBrandFont } from '../api/mutations';
import { fontContentHash } from './content-hash';
import { fontFamilyOf, readFontMetadata } from './font-metadata';

export interface FontUpload {
  upload: (files: readonly File[], folderId?: string) => void;
  isUploading: boolean;
  failure: string | null;
  dismissFailure: () => void;
}

/** Same shape `useMediaUpload` takes -- an entity may not import another entity's hook at runtime. */
export interface AssetUploader {
  mutateAsync: (file: File) => Promise<{ id: string }>;
  isPending: boolean;
}

/**
 * Puts a font in the library, in the two steps that actually take: the bytes go to assets-api,
 * then the row goes to guidelines-api. Mirrors `useMediaUpload` exactly -- one file at a time, a
 * failure stops the run rather than being retried silently.
 *
 * The file is also read on the way past, for the family, foundry and licence it declares about
 * itself. This is the only moment those are cheap: the bytes are in hand, and every later reader
 * would have to download the font again to learn the same six strings.
 */
export function useFontUpload(uploadAsset: AssetUploader): FontUpload {
  const createFont = useCreateBrandFont();
  const [failure, setFailure] = useState<string | null>(null);

  const run = async (files: readonly File[], folderId?: string): Promise<void> => {
    setFailure(null);

    for (const file of files) {
      try {
        const metadata = await readFontMetadata(file);
        const contentHash = await fontContentHash(await file.arrayBuffer());
        const asset = await uploadAsset.mutateAsync(file);
        await createFont.mutateAsync({
          assetId: asset.id,
          filename: file.name,
          ...metadata,
          contentHash,
          // A width variant is filed under its own family, because family + weight + style has
          // nowhere to record one: a Condensed Bold filed as plain Bold is the same row twice.
          ...(fontFamilyOf(metadata) ? { familyName: fontFamilyOf(metadata) } : {}),
          // Files it on the way in, so an upload started inside a folder lands there rather than at
          // the top for somebody to move afterwards.
          ...(folderId !== undefined ? { folderId } : {}),
        });
      } catch (error) {
        setFailure(error instanceof Error ? error.message : String(error));
        return;
      }
    }
  };

  return {
    upload: (files, folderId) => void run(files, folderId),
    isUploading: uploadAsset.isPending || createFont.isPending,
    failure,
    dismissFailure: () => setFailure(null),
  };
}
